import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useAuth from '../auth/useAuth';
import { mediaUrl, uploadMedia } from '../api';
import SidebarPanel from './SidebarPanel';
import TagsEditor from './TagsEditor';
import Button from './ui/Button';
import FileButton from './ui/FileButton';

function EditorInfoItem({ icon, label, children }) {
  return (
    <div className="article-info-item">
      <span className="article-info-icon">
        <i className={icon} />
      </span>

      <span className="article-info-label">{label}</span>

      <span className="article-info-value">{children}</span>
    </div>
  );
}

/**
 * The boxes on the right of an editor: what is being written about, tags,
 * department, cover image and summary. Shared by every editing surface — creating and editing an article,
 * creating and editing a news item — so the four screens cannot drift apart
 * again, which is exactly what happened when they were copies.
 *
 * Department follows the pattern the old owner control used: a dropdown when
 * the author may actually choose, plain text when there is nothing to choose.
 */
function ArticleEditorSidebar({
  /* the article being edited; null while a new one is being written */
  article = null,

  // Article (false), news (true) or not chosen yet (null). The box appears
  // only while creating (onKindChange given): an existing item keeps its kind.
  isNews = false,
  onKindChange = null,
  canWriteArticles = true,
  canWriteNews = true,

  // Everything folded on arrival - a plugin editor needs the room.
  allCollapsed = false,

  tags,
  onTagsChange,

  departments = [],
  departmentId,
  onDepartmentChange,
  currentDepartmentName = null,

  icon,
  onIconChange,

  summary,
  onSummaryChange,

  onAISummary,
  isAnalyzingWithAI = false,
  aiError = '',

  onAISuggestTags,
  aiTagSuggestions = [],
  onAcceptAITag,
  isSuggestingTags = false,
  aiTagsError = '',
  onRemoveAITag,

  // 'article' or 'news' - which right the icon upload needs.
  uploadTarget = 'article',

  disabled = false,
}) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [isUploadingIcon, setUploadingIcon] = useState(false);
  const [iconError, setIconError] = useState('');

  // One department means nothing to decide; none means the author writes
  // outside departments entirely. Only several make a control meaningful.
  const canChooseDepartment = departments.length > 1;

  async function handleIconChange(event) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setIconError(t('articleEditor.iconMustBeImage'));
      event.target.value = '';
      return;
    }

    try {
      setUploadingIcon(true);
      setIconError('');

      const uploaded = await uploadMedia(file, uploadTarget);

      // Stored as a path, not an absolute URL: an absolute one pins the item
      // to whichever host uploaded it. The server answers with a path already;
      // resolving against the page keeps an older absolute answer working too.
      onIconChange(new URL(uploaded.url, window.location.origin).pathname);
    } catch (error) {
      console.error('Failed to upload icon:', error);
      setIconError(error?.message || t('articleEditor.iconUploadError'));
    } finally {
      setUploadingIcon(false);
      event.target.value = '';
    }
  }

  // A new article has no dates yet - a dash says that better than "unknown".
  const formatDate = (seconds) => {
    if (!seconds) {
      return article ? t('articleInfoCard.unknown') : '—';
    }

    return new Intl.DateTimeFormat(i18n.language, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(new Date(Number(seconds) * 1000));
  };

  // A new article has no author yet: it will be whoever is writing it.
  const signedInName = [user?.firstName, user?.lastName]
    .filter((part) => part?.trim())
    .join(' ')
    .trim();

  const authorName =
    article?.author?.name?.trim() ||
    article?.author?.nickname?.trim() ||
    signedInName ||
    user?.username?.trim() ||
    t('articleInfoCard.unknownAuthor');

  const selectedDepartment = departments?.find(
    (department) =>
      String(department.department_id) === String(departmentId)
  );

  return (
    <>
      {/* Open on arrival, the others folded away: the details say what is
          being edited, the rest are only needed once something is changed. */}
      <SidebarPanel
        icon="fa-solid fa-circle-info"
        title={t(article ? 'articleInfoCard.title' : 'articleInfoCard.contentTitle')}
        modifierClass="article-info-box"
        collapsible
        defaultOpen={!allCollapsed}
      >
        <div className="article-info-list">
          <EditorInfoItem
            icon="fa-solid fa-user"
            label={t('articleInfoCard.author')}
          >
            {authorName}
          </EditorInfoItem>

          <EditorInfoItem
            icon="fa-solid fa-calendar-plus"
            label={t('articleInfoCard.created')}
          >
            {formatDate(article?.creation_date)}
          </EditorInfoItem>

          <EditorInfoItem
            icon="fa-solid fa-pen-to-square"
            label={t('articleInfoCard.updated')}
          >
            {formatDate(article?.modified_date)}
          </EditorInfoItem>
        </div>
      </SidebarPanel>

      {onKindChange && (
        <SidebarPanel
          icon="fa-solid fa-layer-group"
          title={t('articleEditor.kindLabel')}
          modifierClass="article-editor-box"
          collapsible
          defaultOpen={!allCollapsed && isNews === null}
        >
          <div
            className="article-editor-kind"
            role="group"
            aria-label={t('articleEditor.kindLabel')}
          >
            <Button
              variant={isNews === false ? 'primary' : 'secondary'}
              aria-pressed={isNews === false}
              disabled={disabled || !canWriteArticles}
              onClick={() => onKindChange(false)}
            >
              {t('articlesPage.regularArticle')}
            </Button>

            <Button
              variant={isNews === true ? 'primary' : 'secondary'}
              aria-pressed={isNews === true}
              disabled={disabled || !canWriteNews}
              onClick={() => onKindChange(true)}
            >
              {t('articlesPage.newsArticle')}
            </Button>
          </div>
        </SidebarPanel>
      )}

      <SidebarPanel
        icon="fa-solid fa-tags"
        title={t('articleEditor.tagsLabel')}
        headerAction={
          <Button
            variant="secondary"
            className="ai-assistant-button"
            title={t('articlesPage.aiSuggestTags')}
            aria-label={t('articlesPage.aiSuggestTags')}
            disabled={disabled || isSuggestingTags}
            onClick={(event) => {
              event.stopPropagation();
              onAISuggestTags?.();
            }}
          >
            <i
              className={
                isSuggestingTags
                  ? 'fa-solid fa-spinner fa-spin'
                  : 'fa-solid fa-wand-magic-sparkles'
              }
              aria-hidden="true"
            />
          </Button>
        }
        modifierClass="article-editor-box"
        collapsible
        defaultOpen={false}
      >
        <TagsEditor
          id="article-edit-tags"
          tags={tags}
          onChange={onTagsChange}
          disabled={disabled}
          protectedTag={
            selectedDepartment?.name ||
            currentDepartmentName ||
            (departments.length === 1 ? departments[0]?.name : null)
          }
        />
        {aiTagsError && (
          <div className="ai-assistant-error" role="alert">
            {aiTagsError}
          </div>
        )}

        {aiTagSuggestions.length > 0 && (
          <div className="ai-tag-suggestions">

            {aiTagSuggestions.map((suggestion) => {
              const alreadyAdded = (tags || []).some(
                (tag) =>
                  tag.toLowerCase() === suggestion.name.toLowerCase()
              );


              return (
                <div
                  key={suggestion.name}
                  className="ai-tag-suggestion"
                >
                  <Button
                    variant="secondary"
                    className="ai-tag-action-button"
                    title={
                      alreadyAdded
                        ? t('articleEditor.removeTag')
                        : t('articleEditor.addTag')
                    }
                    aria-label={
                      alreadyAdded
                        ? t('articleEditor.removeTag')
                        : t('articleEditor.addTag')
                    }
                    onClick={() => {
                      if (alreadyAdded) {
                        onRemoveAITag?.(suggestion.name);
                      } else {
                        onAcceptAITag?.(suggestion.name);
                      }
                    }}
                  >
                    <i
                      className={
                        alreadyAdded
                          ? 'fa-solid fa-minus'
                          : 'fa-solid fa-plus'
                      }
                      aria-hidden="true"
                    />
                  </Button>

                  <span className="tag-text">
                    #{suggestion.name}
                  </span>

                  {suggestion.source === 'new' && (
                    <i
                      className="fa-solid fa-wand-sparkles ai-new-tag-icon"
                      title={t('articleEditor.newTagSuggestion')}
                      aria-label={t('articleEditor.newTagSuggestion')}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SidebarPanel>

      <SidebarPanel
        icon="fa-solid fa-sitemap"
        title={t('articleEditor.departmentLabel')}
        modifierClass="article-editor-box"
        collapsible
        defaultOpen={false}
      >
        {canChooseDepartment ? (
          <div className="article-scope-control">
            <i className="fa-solid fa-sitemap" aria-hidden="true" />

            <select
              className="article-scope-select"
              value={departmentId ?? ''}
              disabled={disabled}
              onChange={(event) =>
                onDepartmentChange(
                  event.target.value === ''
                    ? null
                    : Number(event.target.value),
                )
              }
            >
              <option value="">
                {t('articleEditor.departmentPlaceholder')}
              </option>

              {departments.map((department) => (
                <option
                  key={department.department_id}
                  value={department.department_id}
                >
                  {department.name}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <span className="article-info-value">
            {currentDepartmentName ||
              departments[0]?.name ||
              t('articleEditor.noDepartment')}
          </span>
        )}
      </SidebarPanel>

      <SidebarPanel
        icon="fa-solid fa-image"
        title={t('articleEditor.iconLabel')}
        modifierClass="article-editor-box"
        collapsible
        defaultOpen={false}
      >
        {icon && (
          <img
            className="article-editor-icon-preview"
            src={mediaUrl(icon)}
            alt={t('articleEditor.iconPreviewAlt')}
            width="96"
            height="96"
          />
        )}

        <div className="article-editor-icon-actions">
          <FileButton
            id="article-editor-icon"
            variant="secondary"
            accept="image/*"
            disabled={disabled || isUploadingIcon}
            onChange={handleIconChange}
          >
            {isUploadingIcon
              ? t('articleEditor.iconUploading')
              : t('articleEditor.iconUpload')}
          </FileButton>

          {icon && (
            <Button
              variant="secondary"
              disabled={disabled}
              onClick={() => onIconChange(null)}
            >
              {t('articleEditor.iconRemove')}
            </Button>
          )}
        </div>

        {iconError && (
          <div className="login-message login-message-error" role="alert">
            <i className="fa-solid fa-circle-exclamation" />
            <span>{iconError}</span>
          </div>
        )}
      </SidebarPanel>

      <SidebarPanel
        icon="fa-solid fa-align-left"
        title={t('articleEditor.summaryLabel')}
        disabled={disabled || isAnalyzingWithAI}
        modifierClass="article-editor-box"
        collapsible
        defaultOpen={false}
        headerAction={
          <Button
            variant="secondary"
            className="ai-assistant-button"
            title={t('articlesPage.aiGenerateSummary')}
            aria-label={t('articlesPage.aiGenerateSummary')}
            disabled={disabled || isAnalyzingWithAI}
            onClick={(event) => {
              event.stopPropagation();
              onAISummary?.();
            }}
          >
            <i
              className={
                isAnalyzingWithAI
                  ? 'fa-solid fa-spinner fa-spin'
                  : 'fa-solid fa-wand-magic-sparkles'
              }
              aria-hidden="true"
            />
          </Button>
        }

      >
        <textarea
          id="article-editor-summary"
          className="article-editor-summary"
          rows={4}
          maxLength={1000}
          value={summary ?? ''}
          disabled={disabled}
          placeholder={t('articleEditor.summaryPlaceholder')}
          onChange={(event) => onSummaryChange(event.target.value)}
        />

        {aiError && (
          <div className="ai-assistant-error" role="alert">
            {aiError}
          </div>
        )}
      </SidebarPanel>
    </>
  );
}

export default ArticleEditorSidebar;
